// Diyet planı biçim yardımcıları — sunucu karşılığı:
// Pupill-IOT-Server/src/modules/diet/domain/diet-plan.totals.js (aynı hesap; elle senkron).

export const MEAL_TYPES = [
  { value: "breakfast", label: "Kahvaltı", time: "08:00" },
  { value: "snack1", label: "Ara öğün", time: "10:30" },
  { value: "lunch", label: "Öğle", time: "13:00" },
  { value: "snack2", label: "İkindi", time: "16:00" },
  { value: "dinner", label: "Akşam", time: "19:00" },
  { value: "night", label: "Gece", time: "22:00" },
];
export const MEAL_LABEL = Object.fromEntries(MEAL_TYPES.map((m) => [m.value, m.label]));

export const WEEK_DAYS = ["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar"];

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const round1 = (v) => Math.round(v * 10) / 10;

export function mealTotals(meal) {
  const t = { calories: 0, protein: 0, carbs: 0, fat: 0 };
  for (const it of meal?.items || []) {
    t.calories += num(it.calories);
    t.protein += num(it.protein);
    t.carbs += num(it.carbs);
    t.fat += num(it.fat);
  }
  return { calories: Math.round(t.calories), protein: round1(t.protein), carbs: round1(t.carbs), fat: round1(t.fat) };
}

export function dayTotals(day) {
  return (day?.meals || []).map(mealTotals).reduce(
    (a, m) => ({
      calories: a.calories + m.calories,
      protein: round1(a.protein + m.protein),
      carbs: round1(a.carbs + m.carbs),
      fat: round1(a.fat + m.fat),
    }),
    { calories: 0, protein: 0, carbs: 0, fat: 0 }
  );
}

/** Katalog besininden öğün kalemi (katalog miktarı varsayılan; değerler kopyalanır). */
export function itemFromCatalog(food) {
  const id = food?._id || food?.id;
  return {
    food: id || null,
    name: food?.name || "",
    amount: food?.amount ?? "",
    amountUnit: food?.amountUnit || "",
    calories: food?.calories ?? null,
    protein: food?.protein ?? null,
    carbs: food?.carbs ?? null,
    fat: food?.fat ?? null,
    notes: "",
  };
}

/** Miktar değişince besin değerleri aynı oranda ölçeklenir. */
export function rescaleItem(item, nextAmount) {
  const prev = num(item.amount);
  const next = num(nextAmount);
  if (!(prev > 0) || !(next > 0)) return { ...item, amount: nextAmount };
  const f = next / prev;
  const sc = (v) => (v == null || v === "" ? v : round1(num(v) * f));
  return {
    ...item,
    amount: nextAmount,
    calories: item.calories == null || item.calories === "" ? item.calories : Math.round(num(item.calories) * f),
    protein: sc(item.protein),
    carbs: sc(item.carbs),
    fat: sc(item.fat),
  };
}

export const formatKcal = (v) => `${Math.round(num(v)).toLocaleString("tr-TR")} kcal`;

/** "Her gün aynı" (1 gün) ↔ haftalık (7 gün) dönüşümü. */
export function toWeekly(days) {
  const base = days?.[0] || { label: "", meals: [] };
  return WEEK_DAYS.map((label, i) => ({ label, meals: (days?.[i]?.meals || base.meals || []).map((m) => ({ ...m, items: [...(m.items || [])] })) }));
}

export function toDaily(days) {
  return [{ label: "Her gün", meals: days?.[0]?.meals || [] }];
}

/** Plan kaydı → editör formu. */
export function planToForm(plan) {
  return {
    title: plan?.title || "Beslenme planı",
    mode: plan?.mode || "meal_plan",
    startDate: plan?.startDate || "",
    endDate: plan?.endDate || "",
    targets: {
      calories: plan?.targets?.calories ?? "",
      protein: plan?.targets?.protein ?? "",
      carbs: plan?.targets?.carbs ?? "",
      fat: plan?.targets?.fat ?? "",
      waterMl: plan?.targets?.waterMl ?? "",
    },
    goal: {
      startWeight: plan?.goal?.startWeight ?? "",
      targetWeight: plan?.goal?.targetWeight ?? "",
      targetDate: plan?.goal?.targetDate || "",
      startWaist: plan?.goal?.startWaist ?? "",
      targetWaist: plan?.goal?.targetWaist ?? "",
    },
    days: (plan?.days || []).map((d) => ({
      label: d.label || "",
      meals: (d.meals || []).map((m) => ({
        type: m.type,
        time: m.time || "",
        note: m.note || "",
        items: (m.items || []).map((it) => ({ ...it, food: it.food?._id || it.food || null })),
      })),
    })),
    allowedList: (plan?.allowedList || []).map((f) => ({ ...f, _id: f.food?._id || f.food, food: f.food?._id || f.food })),
    forbiddenList: (plan?.forbiddenList || []).map((f) => ({ ...f, _id: f.food?._id || f.food, food: f.food?._id || f.food })),
    note: plan?.note || "",
  };
}

const blankToNull = (v) => (v === "" || v == null ? null : Number(v));

/** Editör formu → sunucu gövdesi. */
export function formToPayload(form) {
  const listOut = (list) =>
    (list || []).map((f) => ({
      food: f.food || f._id || null,
      name: f.name || "",
      amount: f.amount === "" ? undefined : f.amount,
      amountUnit: f.amountUnit || undefined,
      notes: f.notes || undefined,
    }));
  return {
    title: form.title,
    mode: form.mode || "meal_plan",
    startDate: form.startDate || "",
    endDate: form.endDate || "",
    note: form.note ?? "",
    targets: Object.fromEntries(Object.entries(form.targets || {}).map(([k, v]) => [k, blankToNull(v)])),
    goal: {
      startWeight: blankToNull(form.goal?.startWeight),
      targetWeight: blankToNull(form.goal?.targetWeight),
      targetDate: form.goal?.targetDate || "",
      startWaist: blankToNull(form.goal?.startWaist),
      targetWaist: blankToNull(form.goal?.targetWaist),
    },
    days: form.days || [],
    allowedList: listOut(form.allowedList),
    forbiddenList: listOut(form.forbiddenList),
  };
}

export const PLAN_STATUS = {
  active: { label: "Aktif", tone: "primary" },
  completed: { label: "Tamamlandı", tone: "success" },
  cancelled: { label: "İptal", tone: "neutral" },
};

export const PLAN_MODE_OPTIONS = [
  { value: "meal_plan", label: "Öğün planlı" },
  { value: "free", label: "Serbest (kalori hedefli)" },
];
